import React, { useCallback } from 'react';

import { Select } from '@aline/common';
import { routes } from '@aline/routes';
import { ButtonBase } from '@mui/material';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import {
  Blocks,
  Bot,
  Building,
  ChartNoAxesColumnIncreasing,
  CircleFadingArrowUp,
  Database,
  DollarSign,
  FileText,
  Group,
  KeyRound,
  Sparkles,
  Users,
} from 'lucide-react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { openLinkFromAnchorClick } from 'src/helpers/url';
import { makeStyles } from 'tss-react/mui';

import {
  INTEGRATIONS_NAV_SECTIONS,
  INTEGRATIONS_SECTION,
} from './integrations/integrations-nav-sections';
import {
  getIntegrationsSectionLink,
  useActiveIntegrationsSection,
  useIntegrationsSectionNav,
} from './integrations/integrations-section-spy';

const useStyles = makeStyles()(theme => ({
  iconSlot: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: 18,
    justifyContent: 'center',
    width: 18,
  },
  navItem: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackgroundSubtle,
    },
    alignItems: 'center',
    borderRadius: theme.spacing(0.75),
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    display: 'flex',
    fontFamily: '"Open Sans", sans-serif',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 500,
    gap: 8,
    justifyContent: 'flex-start',
    lineHeight: 'normal',
    padding: '8px 12px',
    textAlign: 'left',
    width: '100%',
  },
  navItemActive: {
    backgroundColor: theme.appColors.selectedBackgroundNav,
    color: theme.appColors.primary,
  },
  /**
   * The open group is named in colour only. The fill is reserved for the one row that says
   * where you are, so the column never shows two selections.
   */
  navItemActiveParent: {
    color: theme.appColors.primary,
  },
  /**
   * Each row draws its own slice of the rail plus a branch to its fill. Stacked with no gap the
   * slices join into one line, and the last row stops its slice at the branch to close the tree.
   */
  navItemNested: {
    '&::after': {
      backgroundColor: theme.appColors.ruleSubtle,
      content: '""',
      height: 1,
      left: -10,
      position: 'absolute',
      top: '50%',
      width: 10,
    },
    '&::before': {
      backgroundColor: theme.appColors.ruleSubtle,
      bottom: 0,
      content: '""',
      left: -10,
      position: 'absolute',
      top: 0,
      width: 1,
    },
    '&:last-child::before': {
      bottom: '50%',
    },
    marginLeft: 10,
    minHeight: 32,
    padding: '6px 8px',
    position: 'relative',
    width: 'auto',
  },
  navItemNestedActive: {
    '&&': {
      backgroundColor: theme.appColors.selectedBackgroundNav,
      color: theme.appColors.primary,
    },
  },
  nestedGroup: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    marginTop: 4,
    // 12px row padding + half the 18px icon: the rail hangs from the centre of the parent icon.
    paddingLeft: 21,
  },
  sidebar: {
    background: theme.appColors.background2,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: '0 8px 8px 0',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    padding: 8,
  },
}));

interface NavItemConfig {
  dataCy: string;
  icon: LucideIcon;
  label: string;
  /** The route the item stands for. It is active there and on every route below it. */
  value: string;
  /** When true, the tab is only shown to org admins. */
  adminOnly?: boolean;
  children?: NavItemConfig[];
  /** Where the item links to, when that is not `value`. */
  href?: string;
  /** The section of the Integrations page the item scrolls to. */
  sectionId?: string;
}

const navItems: NavItemConfig[] = [
  {
    dataCy: Select.leftNav.admin.org.tab,
    icon: Building,
    label: 'Org',
    value: routes.AdminOrgIndex,
  },
  {
    dataCy: Select.leftNav.admin.users.tab,
    icon: Users,
    label: 'Users',
    value: routes.AdminUserIndex,
  },
  {
    dataCy: Select.leftNav.admin.groups.tab,
    icon: Group,
    label: 'Groups',
    value: routes.AdminGroupIndex,
  },
  {
    children: [
      {
        dataCy: 'admin-agent-personalization-skills',
        icon: Sparkles,
        label: 'Skills',
        value: routes.AdminAgentSkillIndex,
      },
    ],
    dataCy: 'admin-agent-personalization-tab',
    // The section route only redirects to its first page, so link straight to that page.
    href: routes.AdminAgentSkillIndex,
    icon: Bot,
    label: 'Agent Personalization',
    value: routes.AdminAgentPersonalization,
  },
  {
    dataCy: 'admin-contract-types-tab',
    icon: FileText,
    label: 'Contract Types',
    value: routes.AdminContractTypes,
  },
  {
    dataCy: 'admin-api-keys-tab',
    icon: KeyRound,
    label: 'API Keys',
    value: routes.AdminApiKeys,
  },
  {
    dataCy: Select.leftNav.admin.billing.tab,
    icon: DollarSign,
    label: 'Billing',
    value: routes.AdminBilling,
  },
  {
    dataCy: 'admin-usage-tab',
    icon: ChartNoAxesColumnIncreasing,
    label: 'Usage',
    value: routes.AdminUsage,
  },
  {
    // Server-gated to admins (upgradeRequests resolver rejects non-admins), so
    // hide the tab from them rather than let them click into an error.
    adminOnly: true,
    dataCy: 'admin-upgrade-requests-tab',
    icon: CircleFadingArrowUp,
    label: 'Upgrade Requests',
    value: routes.AdminUpgradeRequests,
  },
  {
    // Server-gated to admins (documentMetadataImports resolver rejects
    // non-admins), so hide the tab rather than let them hit a load error.
    adminOnly: true,
    dataCy: 'admin-data-migration-tab',
    icon: Database,
    label: 'Data Migration',
    value: routes.AdminDataMigration,
  },
  {
    children: INTEGRATIONS_NAV_SECTIONS.map(section => ({
      dataCy: section.dataCy,
      icon: section.icon,
      label: section.label,
      sectionId: section.id,
      value: routes.AdminIntegrations,
    })),
    dataCy: Select.leftNav.admin.integrations.tab,
    icon: Blocks,
    label: 'Integrations',
    value: routes.AdminIntegrations,
  },
];

const NavItem = React.memo(function NavItem({
  isActive,
  item,
  nested = false,
}: {
  isActive: boolean;
  item: NavItemConfig;
  nested?: boolean;
}) {
  const { classes } = useStyles();
  const Icon = item.icon;
  const hasChildren = Boolean(item.children);
  const { scrollToSection, selectSection } = useIntegrationsSectionNav();
  const location = useLocation();
  const isOnIntegrations = location.pathname.startsWith(routes.AdminIntegrations);
  const { sectionId } = item;
  const href = sectionId ? getIntegrationsSectionLink(sectionId) : (item.href ?? item.value);
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (sectionId) {
        openLinkFromAnchorClick(event, href, { applyRouteToExistingTab: true, newTab: false });
        if (!event.defaultPrevented) return;
        selectSection(sectionId);
        if (isOnIntegrations) scrollToSection(sectionId);
        return;
      }
      if (location.pathname === href && getIsPlainLeftClick(event)) {
        event.preventDefault();
        return;
      }
      openLinkFromAnchorClick(event, href, { newTab: false });
    },
    [href, isOnIntegrations, location.pathname, scrollToSection, sectionId, selectSection]
  );
  return (
    <ButtonBase
      className={clsx(classes.navItem, {
        [classes.navItemActive]: isActive && !nested && !hasChildren,
        [classes.navItemActiveParent]: isActive && !nested && hasChildren,
        [classes.navItemNested]: nested,
        [classes.navItemNestedActive]: nested && isActive,
      })}
      component={Link}
      data-cy={item.dataCy}
      to={href}
      onClick={handleClick}
    >
      <span className={classes.iconSlot}>
        <Icon size={nested ? 16 : 18} strokeWidth={1.75} />
      </span>
      {item.label}
    </ButtonBase>
  );
});

export function AdminSidebar(): JSX.Element {
  const location = useLocation();
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const isAdmin = currentUser?.role === Apollo.OrgRole.Admin;
  const isAtGroupDetail = useMatch({ end: false, path: routes.AdminGroupDetail });
  const activePath = isAtGroupDetail ? routes.AdminGroupIndex : location.pathname;
  const isOnIntegrations = activePath.startsWith(routes.AdminIntegrations);
  const storedSectionId = useActiveIntegrationsSection();
  const activeSection = isOnIntegrations ? storedSectionId || INTEGRATIONS_SECTION.channels : null;
  const items = navItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <div className={classes.sidebar}>
      {items.map(item => (
        <React.Fragment key={item.value}>
          <NavItem isActive={getIsNavItemActive({ activePath, activeSection, item })} item={item} />
          {item.children && (
            <div className={classes.nestedGroup}>
              {item.children.map(child => (
                <NavItem
                  key={child.sectionId ?? child.value}
                  nested
                  isActive={getIsNavItemActive({ activePath, activeSection, item: child })}
                  item={child}
                />
              ))}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/** A parent is active when one of its children is; a section entry only on its own section. */
function getIsNavItemActive({
  activePath,
  activeSection,
  item,
}: {
  activePath: string;
  activeSection: string | null;
  item: NavItemConfig;
}): boolean {
  if (item.children)
    return item.children.some(child =>
      getIsNavItemActive({ activePath, activeSection, item: child })
    );
  if (!activePath.startsWith(item.value)) return false;
  return !item.sectionId || activeSection === item.sectionId;
}

function getIsPlainLeftClick(event: React.MouseEvent) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}
